import React, { useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { PasswordChecklist } from '../components/PasswordChecklist';
import { useTranslation } from 'react-i18next';
import { trackRegistration } from '../../../lib/tiktokPixel';
import { trackGoogleAdsRegistration } from '../../../lib/googleAds';
import { trackMarketingSignUp, trackMarketingStartTrial } from '../../../lib/marketingEvents';
import { trackMetaCompleteRegistration, trackMetaStartTrial } from '../../../lib/metaPixel';

function valid(pwd){
  return pwd.length>=10 && /[A-Z]/.test(pwd) && /[a-z]/.test(pwd) && /\d/.test(pwd) && /[^A-Za-z0-9]/.test(pwd);
}

export function RegisterForm({ setView, setError, setMessage, setLoading }) {
  const [email,setEmail] = useState('');
  const [pwd,setPwd] = useState('');
  const [showPwd,setShowPwd] = useState(false);
  const { t, i18n } = useTranslation();

  const score = [t('auth.pwd.weak','Weak'),t('auth.pwd.medium','Medium'),t('auth.pwd.medium','Medium'),t('auth.pwd.strong','Strong'),t('auth.pwd.strong','Strong')];
  const pct = Math.min(5,[
    pwd.length>=10,
    /[A-Z]/.test(pwd),
    /[a-z]/.test(pwd),
    /\d/.test(pwd),
    /[^A-Za-z0-9]/.test(pwd)
  ].filter(Boolean).length);

  const onSubmit = async (e)=>{
    e.preventDefault(); setError(''); setMessage(''); setLoading(true);
    console.log('[RegisterForm] 📝 Signup attempt:', { email });
    
    try {
      if(!valid(pwd)) { 
        console.warn('[RegisterForm] ⚠️ Password validation failed');
        setError(t('auth.password.requirements_failed','Password does not meet requirements')); 
        setLoading(false); 
        return; 
      }
      
      console.log('[RegisterForm] 📡 Calling supabase.auth.signUp...');
      const siteUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password: pwd,
        options: {
          emailRedirectTo: siteUrl + `/?confirmed=true&lang=${encodeURIComponent(i18n.language||'en')}`
        }
      });
      if(error) throw error;

      // Supabase email enumeration protection: email ya existe → devuelve 200 con identities=[]
      if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
        console.warn('[RegisterForm] ⚠️ Email enumeration protection: email ya existe');
        setError(t('auth.errors.already_registered', 'This email is already registered. Try signing in or reset your password.'));
        return;
      }

      console.log('[RegisterForm] ✅ Signup SUCCESS, check email');
      trackRegistration(); // TikTok Pixel: CompleteRegistration
      trackGoogleAdsRegistration(); // Google Ads: Registro, only after Supabase accepts signup
      trackMarketingSignUp(); // GTM/dataLayer: map to CompleteRegistration/sign_up
      trackMarketingStartTrial(); // GTM/dataLayer: trial starts after accepted signup
      trackMetaCompleteRegistration({ registration_method: 'email' });
      trackMetaStartTrial({ registration_method: 'email' });
      setMessage(t('auth.check_email','Check your email to confirm your account'));
    } catch(err){ 
      console.error('[RegisterForm] ❌ Signup FAILED:', {
        message: err.message,
        code: err.code,
        status: err.status
      });
      // Mensajes amigables para errores comunes de Supabase
      const raw = (err.message || '').toLowerCase();
      let friendly;
      if (raw.includes('rate limit') || raw.includes('over_email') || err.status === 429) {
        friendly = t('auth.errors.rate_limit', 'Too many attempts. Please wait a few minutes and try again.');
      } else if (raw.includes('already registered') || raw.includes('already exists')) {
        friendly = t('auth.errors.already_registered', 'This email is already registered. Did you forget your password?');
      } else if (raw.includes('invalid email') || raw.includes('invalid format')) {
        friendly = t('auth.errors.invalid_email', 'Invalid email address.');
      } else if (raw.includes('database error') || raw.includes('unexpected_failure')) {
        friendly = t('auth.errors.server_error', 'Server error. Please try again in a few moments.');
      } else {
        friendly = err.message || t('auth.errors.signup_failed', 'Could not sign up. Please try again.');
      }
      setError(friendly);
    } finally { 
      setLoading(false); 
      console.log('[RegisterForm] 🏁 Signup attempt complete');
    }
  };

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="block text-sm text-gray-300 mb-1">{t('auth.email','Email')}</label>
  <input autoFocus type="email" value={email} onChange={e=>setEmail(e.target.value)} required className="input-primary w-full" />
      </div>
      <div>
        <label className="block text-sm text-gray-300 mb-1">{t('auth.password','Password')}</label>
        <div className={`relative rounded-lg`}> 
          <input type={showPwd?'text':'password'} value={pwd} onChange={e=>setPwd(e.target.value)} required className="input-primary w-full pr-10" />
          <button type="button" onClick={()=>setShowPwd(s=>!s)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white">{showPwd? '🙈':'👁️'}</button>
        </div>
        <div className="h-2 mt-2 bg-gray-700 rounded overflow-hidden">
          <div className={`h-full transition-all ${pct<=2?'bg-red-500':pct===3?'bg-yellow-500':'bg-green-500'}`} style={{width:`${(pct/5)*100}%`}}/>
        </div>
        <p className="text-xs mt-1 text-gray-400">{pct===0? '': score[pct-1]}</p>
        <PasswordChecklist password={pwd} />
      </div>
      <div className="flex gap-3 justify-end pt-2">
        <button type="button" onClick={()=>setView('login')} className="btn-secondary">{t('common.cancel','Cancel')}</button>
        <button type="submit" className="btn-primary">{t('auth.signup','Sign up')}</button>
      </div>
    </form>
  );
}
